/*
 * Design note for this component:
 * Cybernetic Editorial Observatory, adapted for legal and operational clarity.
 * Keep the layout asymmetric, disciplined, and text-led, with premium dark surfaces,
 * restrained crimson signals, and strong readable hierarchy for trust content.
 */

import { Button } from "@/components/ui/button";
import type { LucideIcon } from "lucide-react";
import { ArrowLeft, ArrowRight, ExternalLink } from "lucide-react";
import type { ReactNode } from "react";

type PolicyCard = {
  title: string;
  body: string;
  icon: LucideIcon;
};

type PolicySection = {
  id: string;
  title: string;
  intro: string;
  paragraphs: string[];
};

type RelatedLink = {
  label: string;
  href: string;
  description: string;
};

type PolicyLayoutProps = {
  eyebrow: string;
  title: string;
  intro: string;
  highlight: string;
  cards: PolicyCard[];
  sections: PolicySection[];
  relatedLinks: RelatedLink[];
  featurePanel?: ReactNode;
};

export default function PolicyLayout({
  eyebrow,
  title,
  intro,
  highlight,
  cards,
  sections,
  relatedLinks,
  featurePanel,
}: PolicyLayoutProps) {
  return (
    <main className="min-h-screen bg-[radial-gradient(circle_at_top,rgba(124,22,39,0.2),transparent_26%),linear-gradient(180deg,#06080d_0%,#0b0d13_52%,#05070b_100%)] text-white">
      <div className="pointer-events-none absolute inset-0 opacity-30" aria-hidden="true">
        <div className="noise-overlay h-full w-full" />
      </div>

      <div className="relative container py-8 md:py-12">
        <div className="overflow-hidden rounded-[2rem] border border-white/10 bg-[rgba(255,255,255,0.03)] shadow-[0_40px_120px_rgba(0,0,0,0.48)] backdrop-blur-xl">
          <div className="border-b border-white/10 px-6 py-6 md:px-8">
            <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
              <a href="/" className="flex items-center gap-3 text-sm tracking-[0.32em] text-white">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[rgba(255,255,255,0.14)] bg-[rgba(131,12,28,0.24)] shadow-[0_0_30px_rgba(131,12,28,0.28)]">
                  <span className="font-display text-lg">C</span>
                </span>
                <span className="uppercase text-white/78">Command Secrets</span>
              </a>

              <div className="flex flex-wrap items-center gap-3">
                <Button
                  variant="outline"
                  className="border-white/12 text-xs uppercase tracking-[0.24em] text-white/78 hover:bg-white/8 hover:text-white"
                  asChild
                >
                  <a href="/support">
                    Support
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </a>
                </Button>
                <Button
                  variant="outline"
                  className="border-white/12 text-xs uppercase tracking-[0.24em] text-white/78 hover:bg-white/8 hover:text-white"
                  asChild
                >
                  <a href="/">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Return home
                  </a>
                </Button>
              </div>
            </div>
          </div>

          <section className="relative border-b border-white/10 px-6 py-10 md:px-8 md:py-12">
            <div className="absolute -right-20 top-0 h-56 w-56 rounded-full bg-[rgba(145,19,36,0.18)] blur-3xl" />
            <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-start">
              <div className="max-w-3xl">
                <p className="eyebrow">{eyebrow}</p>
                <h1 className="mt-4 font-display text-4xl leading-tight text-white md:text-6xl">{title}</h1>
                <p className="mt-6 max-w-2xl text-base leading-8 text-white/68 md:text-lg">{intro}</p>
              </div>

              <div className="rounded-[1.8rem] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.06),rgba(255,255,255,0.02))] p-6 shadow-[0_20px_80px_rgba(0,0,0,0.36)]">
                <p className="text-[11px] uppercase tracking-[0.32em] text-[var(--signal-soft)]">Operating emphasis</p>
                <p className="mt-4 font-display text-3xl leading-tight text-white">{highlight}</p>
                <p className="mt-4 text-sm leading-7 text-white/64">
                  These public pages are written to be clear, strict, and commercially readable. They explain how Command Secrets handles
                  legal boundaries, customer requests, data safeguards, and protected intellectual property without exposing proprietary
                  calculation logic.
                </p>
              </div>
            </div>
          </section>

          <section className="border-b border-white/10 px-6 py-8 md:px-8">
            <div className="grid gap-4 md:grid-cols-3">
              {cards.map((card) => {
                const Icon = card.icon;
                return (
                  <article
                    key={card.title}
                    className="rounded-[1.35rem] border border-white/10 bg-[rgba(255,255,255,0.03)] p-5 shadow-[0_20px_60px_rgba(0,0,0,0.22)]"
                  >
                    <Icon className="h-5 w-5 text-[var(--signal-soft)]" />
                    <h2 className="mt-4 font-display text-2xl text-white">{card.title}</h2>
                    <p className="mt-3 text-sm leading-7 text-white/64">{card.body}</p>
                  </article>
                );
              })}
            </div>
          </section>

          {featurePanel ? <section className="border-b border-white/10 px-6 py-8 md:px-8">{featurePanel}</section> : null}

          <section className="px-6 py-8 md:px-8 md:py-10">
            <div className="grid gap-8 lg:grid-cols-[0.34fr_0.66fr] lg:items-start">
              <aside className="lg:sticky lg:top-8">
                <div className="rounded-[1.6rem] border border-white/10 bg-[rgba(255,255,255,0.03)] p-6">
                  <p className="text-[11px] uppercase tracking-[0.32em] text-[var(--signal-soft)]">On this page</p>
                  <nav className="mt-5 space-y-3 text-sm">
                    {sections.map((section, index) => (
                      <a
                        key={section.id}
                        href={`#${section.id}`}
                        className="flex items-start gap-3 rounded-xl border border-transparent px-3 py-3 text-white/68 transition hover:border-white/10 hover:bg-white/4 hover:text-white"
                      >
                        <span className="mt-0.5 text-[10px] uppercase tracking-[0.3em] text-[var(--signal-soft)]">0{index + 1}</span>
                        <span className="leading-6">{section.title}</span>
                      </a>
                    ))}
                  </nav>
                </div>
              </aside>

              <div className="space-y-6">
                {sections.map((section) => (
                  <article
                    id={section.id}
                    key={section.id}
                    className="scroll-mt-28 rounded-[1.7rem] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.045),rgba(255,255,255,0.02))] p-6 shadow-[0_24px_80px_rgba(0,0,0,0.24)] md:p-8"
                  >
                    <p className="eyebrow">Section</p>
                    <h2 className="mt-4 font-display text-3xl leading-tight text-white md:text-4xl">{section.title}</h2>
                    <p className="mt-5 text-base leading-8 text-white/72">{section.intro}</p>
                    <div className="mt-6 space-y-5 text-sm leading-8 text-white/66 md:text-base">
                      {section.paragraphs.map((paragraph) => (
                        <p key={paragraph}>{paragraph}</p>
                      ))}
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section className="border-t border-white/10 px-6 py-8 md:px-8">
            <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
              <div className="max-w-2xl">
                <p className="eyebrow">Related surfaces</p>
                <h2 className="mt-4 font-display text-3xl text-white md:text-4xl">Support, legal notice, cancellation terms, and security posture stay connected.</h2>
                <p className="mt-4 text-sm leading-7 text-white/64 md:text-base">
                  Customers should not have to guess where to find billing help, privacy information, or the boundaries around data use,
                  public-figure references, and protected analytical outputs.
                </p>
              </div>
            </div>

            <div className="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              {relatedLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="group rounded-[1.3rem] border border-white/10 bg-[rgba(255,255,255,0.03)] p-5 transition hover:border-[rgba(191,59,80,0.34)] hover:bg-[rgba(131,12,28,0.08)]"
                >
                  <div className="flex items-center justify-between gap-4">
                    <h3 className="font-display text-2xl text-white">{link.label}</h3>
                    <ExternalLink className="h-4 w-4 text-[var(--signal-soft)] transition group-hover:translate-x-0.5" />
                  </div>
                  <p className="mt-3 text-sm leading-7 text-white/64">{link.description}</p>
                </a>
              ))}
            </div>
          </section>
        </div>
      </div>
    </main>
  );
}
